import { formatTimeRangeInTimeZone } from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";

export interface SessionDay {
  startDate: string;
  endDate: string;
}

export interface SessionDayGroup {
  /**
   * Compact, timezone-aware date label, e.g. "Oct 27–29, 2026",
   * "Oct 30 – Nov 2, 2026", or single day "Nov 5, 2026".
   */
  dateLabel: string;
  /**
   * Timezone-aware time range with the timezone short name appended,
   * e.g. "8:00 AM – 4:00 PM (CT)".
   */
  timeLabel: string;
}

/**
 * Returns the calendar date in the given timezone as a `YYYY-MM-DD` string.
 * Comparable lexicographically and convertible to a Date for day arithmetic.
 */
function calendarDayInTimeZone(
  dateString: string,
  timeZone: string | null,
): string {
  const date = new Date(dateString);
  if (!timeZone) {
    // Local calendar day.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  // en-CA yields YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Returns the time-of-day (HH:mm) in the given timezone — used to detect a
 * change in start/end times between consecutive days.
 */
function timeOfDayInTimeZone(
  dateString: string,
  timeZone: string | null,
): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || undefined,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

/** True when `next` is the calendar day immediately after `prev`. */
function isNextCalendarDay(prevDay: string, nextDay: string): boolean {
  const prev = new Date(`${prevDay}T00:00:00Z`);
  const expected = new Date(prev.getTime() + 24 * 60 * 60 * 1000);
  const y = expected.getUTCFullYear();
  const m = String(expected.getUTCMonth() + 1).padStart(2, "0");
  const d = String(expected.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}` === nextDay;
}

/** Month + day, timezone-aware: "Oct 27". */
function monthDay(dateString: string, timeZone: string | null): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || undefined,
    month: "short",
    day: "numeric",
  }).format(new Date(dateString));
}

/** Month only, timezone-aware: "Oct". */
function monthShort(dateString: string, timeZone: string | null): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || undefined,
    month: "short",
  }).format(new Date(dateString));
}

/** Day-of-month only, timezone-aware: "27". */
function dayNumber(dateString: string, timeZone: string | null): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || undefined,
    day: "numeric",
  }).format(new Date(dateString));
}

/** Year only, timezone-aware: "2026". */
function yearNumber(dateString: string, timeZone: string | null): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || undefined,
    year: "numeric",
  }).format(new Date(dateString));
}

/**
 * Compact, timezone-aware date-range label:
 *  - single day          → "Nov 5, 2026"
 *  - same month          → "Oct 27–29, 2026"
 *  - cross-month, same yr → "Oct 30 – Nov 2, 2026"
 *  - cross-year          → "Dec 30, 2026 – Jan 2, 2027"
 */
function formatCompactDateRange(
  startDate: string,
  endDate: string,
  timeZone: string | null,
): string {
  const startDay = calendarDayInTimeZone(startDate, timeZone);
  const endDay = calendarDayInTimeZone(endDate, timeZone);

  const startYear = yearNumber(startDate, timeZone);
  const endYear = yearNumber(endDate, timeZone);

  if (startDay === endDay) {
    return `${monthDay(startDate, timeZone)}, ${startYear}`;
  }

  if (startYear !== endYear) {
    return `${monthDay(startDate, timeZone)}, ${startYear} – ${monthDay(
      endDate,
      timeZone,
    )}, ${endYear}`;
  }

  if (monthShort(startDate, timeZone) === monthShort(endDate, timeZone)) {
    // Same month: "Oct 27–29, 2026" (en dash, no spaces between days).
    return `${monthDay(startDate, timeZone)}–${dayNumber(
      endDate,
      timeZone,
    )}, ${startYear}`;
  }

  // Cross-month, same year: "Oct 30 – Nov 2, 2026".
  return `${monthDay(startDate, timeZone)} – ${monthDay(
    endDate,
    timeZone,
  )}, ${startYear}`;
}

/**
 * Groups a sorted list of session days into contiguous date ranges for
 * stacked rendering. A new group begins whenever there is a calendar-day gap
 * (compared in the event timezone) OR the start/end time-of-day changes vs.
 * the previous day.
 *
 * Each group yields a compact `dateLabel` and a `timeLabel` with the
 * timezone short name appended (e.g. "8:00 AM – 4:00 PM (CT)").
 *
 * Returns an empty array when there are no days.
 */
export function groupSessionDays(
  days: SessionDay[] | null | undefined,
  timezoneIdentifier: string | null,
  timezoneShortName?: string | null,
): SessionDayGroup[] {
  if (!days || days.length === 0) {
    return [];
  }

  const groups: SessionDay[][] = [];
  let current: SessionDay[] = [days[0]];

  for (let i = 1; i < days.length; i += 1) {
    const prev = days[i - 1];
    const day = days[i];

    const prevCalDay = calendarDayInTimeZone(
      prev.startDate,
      timezoneIdentifier,
    );
    const calDay = calendarDayInTimeZone(day.startDate, timezoneIdentifier);

    const contiguous = isNextCalendarDay(prevCalDay, calDay);
    const sameStartTime =
      timeOfDayInTimeZone(prev.startDate, timezoneIdentifier) ===
      timeOfDayInTimeZone(day.startDate, timezoneIdentifier);
    const sameEndTime =
      timeOfDayInTimeZone(prev.endDate, timezoneIdentifier) ===
      timeOfDayInTimeZone(day.endDate, timezoneIdentifier);

    if (contiguous && sameStartTime && sameEndTime) {
      current.push(day);
    } else {
      groups.push(current);
      current = [day];
    }
  }
  groups.push(current);

  return groups.map((group) => {
    const first = group[0];
    const last = group[group.length - 1];
    const dateLabel = formatCompactDateRange(
      first.startDate,
      last.startDate,
      timezoneIdentifier,
    );
    // formatTimeRangeInTimeZone joins with a hyphen; use an en dash for the
    // time range to match the rest of the event-registration UI.
    const timeRange = formatTimeRangeInTimeZone(
      first.startDate,
      first.endDate,
      timezoneIdentifier,
    ).replace(" - ", " – ");
    const timeLabel = timezoneShortName
      ? `${timeRange} (${timezoneShortName})`
      : timeRange;
    return { dateLabel, timeLabel };
  });
}
